import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { FiArrowRight, FiGlobe, FiMapPin, FiShare2 } from "react-icons/fi";
import {
  FaSpotify,
  FaYoutube,
  FaInstagram,
  FaFacebookF,
  FaTwitter,
  FaApple,
  FaSoundcloud,
} from "react-icons/fa";

import MainContainer from "../../../components/utils/MainContainer";
import Seo, { seoExcerpt } from "../../../components/utils/Seo";
import {
  HeroDetailSkeleton,
  ProductGridSkeleton,
} from "../../../components/utils/Skeleton";
import ProductCard from "../../../components/portal/ProductCard";
import NewsCard from "../../../components/portal/NewsCard";
import { toast } from "../../../components/modern-ui/sonner";
import { formatDateNoHours, formatDatePeriod } from "../../../utils/utils";
import useReveal from "../../../hooks/useReveal";

import productsService from "../../../api/services/products";
import newsService from "../../../api/services/news";

import { selectPublicFestivalDetails } from "../../../store/slices/festivals/slice";
import { getFestivalPublicDetails } from "../../../store/slices/festivals/asyncThunks";

import {
  Hero,
  HeroInner,
  HeroName,
  HeroMetaRow,
  HeroActions,
  SocialButton,
  Page,
  Section,
  SectionLabel,
  SectionH2,
  Prose,
  ProductsGrid,
  NewsGrid,
  RevealCell,
  SectionFooter,
  GhostButton,
  EmptyNote,
  StateContainer,
} from "../BandDetailsPage/BandDetailsPageNew.styles";

const SOCIAL_ICONS = {
  spotify: FaSpotify,
  youtube: FaYoutube,
  instagram: FaInstagram,
  facebook: FaFacebookF,
  twitter: FaTwitter,
  apple: FaApple,
  soundcloud: FaSoundcloud,
  website: FiGlobe,
};

// Backend za listu proizvoda vraća grupisan format po kategorijama
const flattenProducts = (data) => {
  if (!Array.isArray(data)) return [];
  if (data[0]?.categoryName && data[0]?.products) {
    return data.flatMap((category) =>
      Array.isArray(category.products) ? category.products : [],
    );
  }
  return data;
};

const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

/* Grid ćelija sa sopstvenim scroll-reveal observerom; --i je kolona u redu */
const RevealGridCell = ({ columnIndex, children }) => {
  const [ref, visible] = useReveal({ rootMargin: "0px 0px -40px 0px" });
  return (
    <RevealCell ref={ref} $visible={visible} style={{ "--i": columnIndex }}>
      {children}
    </RevealCell>
  );
};

export default function FestivalDetailsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    data: festivalData,
    loading,
    error,
  } = useSelector(selectPublicFestivalDetails);

  const { festivalId } = useParams();

  const [products, setProducts] = useState(null); // null = učitava se
  const [festivalNews, setFestivalNews] = useState([]);

  // Scroll-reveal po sekcijama
  const [bioRef, bioVisible] = useReveal();
  const [eventsRef, eventsVisible] = useReveal();
  const [merchRef, merchVisible] = useReveal();
  const [newsRef, newsVisible] = useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (festivalId) {
      dispatch(getFestivalPublicDetails(festivalId));
    }
  }, [dispatch, festivalId]);

  // Merch festivala, svi proizvodi (backend paginira po 12)
  useEffect(() => {
    if (!festivalData?.id) return undefined;
    let active = true;

    const loadProducts = async () => {
      try {
        const first = await productsService.getProducts({
          page: 1,
          festivalId: festivalData.id,
        });
        const firstItems = flattenProducts(first?.data);
        const perPage = Number(first?.perPage) || 12;
        const total = Number(first?.total) || firstItems.length;
        const totalPages = Math.min(Math.ceil(total / perPage) || 1, 10);

        let rest = [];
        if (totalPages > 1) {
          const responses = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              productsService.getProducts({
                page: i + 2,
                festivalId: festivalData.id,
              }),
            ),
          );
          rest = responses.flatMap((response) =>
            flattenProducts(response?.data),
          );
        }

        if (!active) return;
        const seen = new Set();
        setProducts(
          [...firstItems, ...rest].filter((product) => {
            if (!product?.id || seen.has(product.id)) return false;
            seen.add(product.id);
            return true;
          }),
        );
      } catch {
        if (active) setProducts([]);
      }
    };

    loadProducts();
    return () => {
      active = false;
    };
  }, [festivalData?.id]);

  // Novosti festivala, iz najnovijih novosti (API nema filter po festivalu)
  useEffect(() => {
    if (!festivalData?.id) return undefined;
    let active = true;

    const loadNews = async () => {
      try {
        const [page1, page2] = await Promise.all([
          newsService.getNews({ page: 1 }),
          newsService.getNews({ page: 2 }).catch(() => null),
        ]);
        if (!active) return;
        const all = [...(page1?.data || []), ...(page2?.data || [])];
        setFestivalNews(
          all
            .filter(
              (item) => String(item.festivalId) === String(festivalData.id),
            )
            .slice(0, 3),
        );
      } catch {
        if (active) setFestivalNews([]);
      }
    };

    loadNews();
    return () => {
      active = false;
    };
  }, [festivalData?.id]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${festivalData.name}`,
          text: `Pogledajte informacije o festivalu ${festivalData.name}`,
          url: window.location.href,
        });
      } catch (shareError) {
        // Otkazan share dijalog nije greška
        if (shareError.name !== "AbortError") {
          navigator.clipboard.writeText(window.location.href);
          toast.success("Link je kopiran u clipboard");
        }
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link je kopiran u clipboard");
    }
  };

  const goTo = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const socials = useMemo(() => {
    const links = festivalData?.socialLinks || {};
    return Object.entries(SOCIAL_ICONS)
      .filter(([key]) => links[key])
      .map(([key, Icon]) => ({ key, Icon, url: links[key] }));
  }, [festivalData?.socialLinks]);

  const bioParagraphs = useMemo(() => {
    const bio = festivalData?.biography || festivalData?.description || "";
    return bio
      .split(/\n{2,}|\r\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  }, [festivalData?.biography, festivalData?.description]);

  const events = useMemo(() => {
    if (!Array.isArray(festivalData?.events)) return [];
    return [...festivalData.events].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
  }, [festivalData?.events]);

  const coverImage =
    festivalData?.bannerImage ||
    festivalData?.gallery?.find((img) => img.isBanner)?.url ||
    festivalData?.gallery?.[0]?.url ||
    festivalData?.mainImage ||
    null;

  if (loading) {
    return (
      <MainContainer>
        <HeroDetailSkeleton />
      </MainContainer>
    );
  }

  if (error || !festivalData) {
    return (
      <MainContainer>
        <StateContainer>
          {error
            ? `Greška prilikom učitavanja festivala: ${error}`
            : "Festival nije pronađen."}
        </StateContainer>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      {/* SEO, meta iz podataka festivala (bez JSON-LD, samo meta tagovi) */}
      <Seo
        title={festivalData.name}
        description={seoExcerpt(
          festivalData.biography || festivalData.description,
        )}
        canonical={`/festival/${festivalId || festivalData.id}`}
        image={coverImage}
      />
      {/* Hero */}
      <Hero>
        {coverImage && (
          <img className="cover" src={coverImage} alt={festivalData.name} />
        )}
        <HeroInner>
          <HeroName>{festivalData.name}</HeroName>
          <HeroMetaRow>
            <span>Festival</span>
            {festivalData.city && (
              <>
                <span className="dot">·</span>
                <span>{festivalData.city}</span>
              </>
            )}
            {festivalData.founded && (
              <>
                <span className="dot">·</span>
                <span>od {festivalData.founded}.</span>
              </>
            )}
          </HeroMetaRow>
          <HeroActions>
            {socials.map(({ key, Icon, url }) => (
              <SocialButton
                key={key}
                type="button"
                title={key}
                aria-label={key}
                onClick={() =>
                  window.open(url, "_blank", "noopener,noreferrer")
                }
              >
                <Icon size={15} />
              </SocialButton>
            ))}
            <SocialButton
              type="button"
              title="Podeli"
              aria-label="Podeli"
              onClick={handleShare}
            >
              <FiShare2 size={15} />
            </SocialButton>
          </HeroActions>
        </HeroInner>
      </Hero>

      <Page>
        {/* O festivalu */}
        {bioParagraphs.length > 0 && (
          <Section ref={bioRef}>
            <SectionLabel $visible={bioVisible}>O festivalu</SectionLabel>
            <Prose $visible={bioVisible}>
              {bioParagraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </Prose>
          </Section>
        )}

        {/* Događaji */}
        {events.length > 0 && (
          <Section ref={eventsRef}>
            <SectionLabel $visible={eventsVisible}>Događaji</SectionLabel>
            <SectionH2 $visible={eventsVisible}>Događaji</SectionH2>
            <EventsList>
              {events.map((event, index) => {
                const lineup = toList(event.lineup);
                return (
                  <EventRow key={event.id || index}>
                    <div className="date">
                      {event.endDate && event.endDate !== event.date
                        ? formatDatePeriod(event.date, event.endDate)
                        : formatDateNoHours(event.date)}
                    </div>
                    <div className="info">
                      <div className="venue">
                        {event.venue || festivalData.name}
                      </div>
                      {(event.city || event.country) && (
                        <div className="place">
                          <FiMapPin size={12} />
                          {[event.city, event.country]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                      {lineup.length > 0 && (
                        <div className="lineup">{lineup.join(" · ")}</div>
                      )}
                    </div>
                  </EventRow>
                );
              })}
            </EventsList>
          </Section>
        )}

        {/* Merch, jezgro stranice */}
        <Section ref={merchRef}>
          <SectionLabel $visible={merchVisible}>Merch</SectionLabel>
          <SectionH2 $visible={merchVisible}>
            Merch
            {Array.isArray(products) && (
              <small>
                {products.length}{" "}
                {products.length === 1 ? "proizvod" : "proizvoda"}
              </small>
            )}
          </SectionH2>

          {products === null ? (
            <ProductGridSkeleton count={4} />
          ) : products.length === 0 ? (
            <EmptyNote>
              Ovaj festival trenutno nema proizvoda u ponudi.
            </EmptyNote>
          ) : (
            <>
              <ProductsGrid>
                {products.map((product, index) => (
                  <RevealGridCell key={product.id} columnIndex={index % 4}>
                    <ProductCard product={product} />
                  </RevealGridCell>
                ))}
              </ProductsGrid>
              <SectionFooter>
                <GhostButton
                  type="button"
                  onClick={() =>
                    goTo(`/prodavnica?festival=${festivalData.id}`)
                  }
                >
                  Otvori u prodavnici <FiArrowRight size={16} />
                </GhostButton>
              </SectionFooter>
            </>
          )}
        </Section>

        {/* Novosti festivala */}
        {festivalNews.length > 0 && (
          <Section ref={newsRef}>
            <SectionLabel $visible={newsVisible}>Novosti</SectionLabel>
            <SectionH2 $visible={newsVisible}>Novosti festivala</SectionH2>
            <NewsGrid>
              {festivalNews.map((news, index) => (
                <RevealGridCell key={news.id} columnIndex={index % 3}>
                  <NewsCard news={news} />
                </RevealGridCell>
              ))}
            </NewsGrid>
          </Section>
        )}
      </Page>
    </MainContainer>
  );
}

/* --------------------- stilovi specifični za festival --------------------- */

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--border);
`;

const EventRow = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border);

  .date {
    flex: 0 0 170px;
    color: var(--brand);
    font-size: 0.85rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding-top: 2px;
  }

  .info {
    min-width: 0;
  }

  .venue {
    color: var(--text-primary);
    font-weight: 700;
    font-size: 1rem;
  }

  .place {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: var(--text-secondary);
    font-size: 0.85rem;
    margin-top: 0.25rem;
  }

  .lineup {
    color: var(--text-muted);
    font-size: 0.85rem;
    margin-top: 0.35rem;
    line-height: 1.5;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.35rem;

    .date {
      flex: none;
    }
  }
`;
